// src/components/common/BookmarkFavoriteButtons.jsx
import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useBookmarkFavorite } from '../../hooks/useBookmarkFavorite';
import { LoginModal } from '../auth/SignInButton';
/**
 * Bookmark Button Component
 * Shows bookmark icon and handles toggle
 */
export function BookmarkButton({ targetType, targetId, className = '', style = {} }) {
  const {
    isBookmarked,
    isLoadingBookmark,
    toggleBookmark,
    canUseFeatures,
  } = useBookmarkFavorite(targetType, targetId);

  if (!canUseFeatures) {
    return null; // Only show for authenticated USER role
  }

  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleBookmark();
      }}
      disabled={isLoadingBookmark}
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        cursor: isLoadingBookmark ? 'wait' : 'pointer',
        opacity: isLoadingBookmark ? 0.7 : 1,
        ...style,
      }}
      title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
    >
      <svg
        width="17"
        height="20"
        viewBox="0 0 19 21"
        fill={isBookmarked ? '#FF9800' : 'none'}
        stroke={isBookmarked ? '#FF9800' : 'currentColor'}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
      </svg>
      <span style={{ fontSize: '0.9rem' }}>
        {isBookmarked ? 'Bookmarked' : 'Bookmark'}
      </span>
    </button>
  );
}

/**
 * Favorite Button Component (Heart Icon)
 * Shows heart icon and handles toggle
 */
export function FavoriteButton({ targetType, targetId, className = '', style = {} }) {
  const {
    isFavorited,
    isLoadingFavorite,
    toggleFavorite,
    canUseFeatures,
  } = useBookmarkFavorite(targetType, targetId);
  
  const { loginWithRedirect } = useAuth0();
  
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // if (!canUseFeatures) {
  //   return null; // Only show for authenticated USER role
  // }

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canUseFeatures) {
      setShowLoginModal(true);
    } else {
      toggleFavorite();
    }
  };

  return (
     <> 
      <button
        onClick={handleClick}
        disabled={canUseFeatures && isLoadingFavorite}
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          cursor: isLoadingFavorite ? 'wait' : 'pointer',
          opacity: isLoadingFavorite ? 0.7 : 1,
          ...style,
        }}
        title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      >
        <svg width="18" height="21" viewBox="0 0 24 24"
          fill={isFavorited ? '#f44336' : 'none'}
          stroke={isFavorited ? '#f44336' : 'currentColor'}
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
        <span style={{ fontSize: '0.9rem' }}>
          {isFavorited ? 'Favourited' : 'Favourite'}
        </span>
      </button>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={() => loginWithRedirect({ authorizationParams: { prompt: 'login' } })}
        onSignupAsUser={() => loginWithRedirect({ authorizationParams: { screen_hint: 'signup' }, appState: { pendingRole: 'USER' } })}
        onSignupAsFarmer={() => loginWithRedirect({ authorizationParams: { screen_hint: 'signup' }, appState: { pendingRole: 'FARMER' } })}
      />
    </>
  );
}

/**
 * Combined Bookmark & Favorite Buttons
 * Shows both buttons side by side
 */
export function BookmarkFavoriteButtons({ 
  targetType, 
  targetId, 
  className = '',
  layout = 'horizontal' // 'horizontal' | 'vertical'
}) {
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: layout === 'vertical' ? 'column' : 'row',
        gap: '0.75rem',
      }}
    >
      <BookmarkButton targetType={targetType} targetId={targetId} />
      <FavoriteButton targetType={targetType} targetId={targetId} />
    </div>
  );
}

/**
 * Icon-only Bookmark Button (for compact layouts)
 */
export function BookmarkIconButton({ targetType, targetId, className = '', size = 24 }) {
  const {
    isBookmarked,
    isLoadingBookmark,
    toggleBookmark,
    canUseFeatures,
  } = useBookmarkFavorite(targetType, targetId);

  if (!canUseFeatures) {
    return null;
  }

  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleBookmark();
      }}
      disabled={isLoadingBookmark}
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${size + 16}px`,
        height: `${size + 16}px`,
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        background: isBookmarked ? '#fff3e0' : 'rgba(255,255,255,0.9)',
        cursor: isLoadingBookmark ? 'wait' : 'pointer',
        opacity: isLoadingBookmark ? 0.7 : 1,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
      title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={isBookmarked ? '#FF9800' : 'none'}
        stroke={isBookmarked ? '#FF9800' : '#666'}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
      </svg>
    </button>
  );
}

/**
 * Icon-only Favorite Button (for compact layouts)
 */
export function FavoriteIconButton({ targetType, targetId, className = '', size = 24 }) {
  const {
    isFavorited,
    isLoadingFavorite,
    toggleFavorite,
    canUseFeatures,
  } = useBookmarkFavorite(targetType, targetId);

  if (!canUseFeatures) {
    return null;
  }

  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleFavorite();
      }}
      disabled={isLoadingFavorite}
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${size + 16}px`,
        height: `${size + 16}px`,
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        background: isFavorited ? '#ffebee' : 'rgba(255,255,255,0.9)',
        cursor: isLoadingFavorite ? 'wait' : 'pointer',
        opacity: isLoadingFavorite ? 0.7 : 1,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
      title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={isFavorited ? '#f44336' : 'none'}
        stroke={isFavorited ? '#f44336' : '#666'}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    </button>
  );
}
